import { Box, Chip, LinearProgress, Tooltip } from "@mui/material";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import LeaveDocumentsDrawer from "./LeaveDocumentsDrawer";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { fetchDataEngine } from "../../../data/Axios/DynamicService";

import {
  getStructuredLeaveApplications,
} from "../../../data/Queries/HRmanagement/leavemanagement";
import { LeaveApplicationsColumns } from "../../../data/Columns/hrmanagement";
import { HRService } from "../../../config";
import LeaveApplicationForm from "./form";
import { tokens } from "../../../theme";
import { useTheme } from "@emotion/react";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import PregnantWomanIcon from "@mui/icons-material/PregnantWoman";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import EventNoteIcon from "@mui/icons-material/EventNote";

const base_url = HRService.uri;

const LeaveApplicationsTable = () => {
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);
  const [selectedRecordForDocs, setSelectedRecordForDocs] = useState(null);

  const actions = {
    entity: "My Leave Applications",
    isSubRight: true,
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: true,
      permission: "EditPermission",
      // Disable edit for approved/rejected applications
      showCondition: (row) =>
        row?.statusDescription?.toLowerCase() !== "approved" &&
        row?.statusDescription?.toLowerCase() !== "rejected",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: true,
      permission: "CreatePermission",
      inputObjectName: "createCaseDynamic",
      InputObject: "newCase",
    },
    activateDeactivate: {
      key: "ACTIVATEDEACTIVATE",
      button_name: "Deactivate",
      Show_Button: false,
      permission: "ActivatePermission",
    },
    AssignTask: {
      key: "ASSIGN_TASK",
      button_name: "Assign Task",
      Show_Button: false,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4],
    },
    ViewRelievers: {
      key: "MARK_TASK_DONE",
      button_name: "View Relievers",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4],
    },
    approve: {
      key: "APPROVE_LEAVE",
      button_name: "Approve Leave",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2],
    },
    decline: {
      key: "REJECT_LEAVE",
      button_name: "Reject Leave",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2],
    },
    ViewDocuments: {
      key: "ViewDocuments",
      button_name: "View Documents",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4],
      onClick: (row) => {
        setSelectedRecordForDocs(row);
        setDocsDrawerOpen(true);
      },
    },
    // dataObject: {
    //   inputObjectName: "approveDeclineLeaveApplication",
    //   inputSubObjectName: "applicationsApprovals",
    //   inputFields: ["applicationId", "userId", "comments"],
    // },

    actionName: "Leave Application ",
    showStatus: true,
  };
  const [data, setData] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const loggedInEmployeeId = parseInt(decodedToken.EmployeeId);
  useEffect(() => {
    async function fetchLeaveBalances() {
      try {
        const response = await fetchDataEngine(
          "employeeLeaveBalancesBySpecificColumns",
          "columns",
          {
            employeeId: loggedInEmployeeId,
          },
          ["id", "leaveTypeId", "leaveType", "balance"],
          base_url,
        );
        if (response) {
          setData(response);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }

    fetchLeaveBalances();
  }, []);
  const leaveTypeConfig = {
    Annual: { icon: <BeachAccessIcon fontSize="small" />, maxDays: 21 },
    Compansionate: { icon: <FavoriteIcon fontSize="small" />, maxDays: 14 },
    Compassionate: { icon: <FavoriteIcon fontSize="small" />, maxDays: 14 },
    Paternity: { icon: <ChildCareIcon fontSize="small" />, maxDays: 14 },
    Maternity: { icon: <PregnantWomanIcon fontSize="small" />, maxDays: 90 },
    Sick: { icon: <LocalHospitalIcon fontSize="small" />, maxDays: 30 },
  };

  const getConfig = (leaveType) =>
    leaveTypeConfig[leaveType] || { icon: <EventNoteIcon fontSize="small" />, maxDays: 30 };

  const getBalanceColor = (balance) => {
    if (balance === null || balance <= 0) return colors.redAccent[400];
    if (balance <= 5) return "#ed6c02";
    return colors.greenAccent[400];
  };

  const getProgressColor = (balance, maxDays) => {
    const pct = (balance / maxDays) * 100;
    if (pct <= 0) return colors.redAccent[400];
    if (pct <= 25) return "#ed6c02";
    return colors.greenAccent[400];
  };

  const getStatusLabel = (balance) => {
    if (balance === null || balance === undefined) return { label: "N/A", color: "default" };
    if (balance <= 0) return { label: "Exhausted", color: "error" };
    if (balance <= 5) return { label: "Low", color: "warning" };
    return { label: "Available", color: "success" };
  };

  const leaveBalances = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const latestByLeaveType = new Map();
    data.forEach((item) => {
      const key = item.leaveTypeId || item.leaveType;
      const existing = latestByLeaveType.get(key);

      if (!existing || (item.id || 0) > (existing.id || 0)) {
        latestByLeaveType.set(key, item);
      }
    });

    return Array.from(latestByLeaveType.values()).sort(
      (a, b) => (a.leaveTypeId || 0) - (b.leaveTypeId || 0)
    );
  }, [data]);

  return (
    <Box m="2px">
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{ color: colors.grey[100], mb: 1.5, px: 0.5 }}
        >
          Your Leave Balances
        </Typography>
          {leaveBalances.length > 0 ? (
            <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {leaveBalances.map((item, index) => {
              const balance = item.balance ?? 0;
              const config = getConfig(item.leaveType);
              const pct = Math.min((balance / config.maxDays) * 100, 100);
              const balanceColor = getBalanceColor(item.balance);
              const status = getStatusLabel(item.balance);

              return (
                <Tooltip
                  key={index}
                  title={`${item.leaveType} Leave: ${balance} of ${config.maxDays} days`}
                  arrow
                >
                  <Card
                    variant="outlined"
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 140,
                      maxWidth: 200,
                      backgroundColor: colors.primary[400],
                      borderTop: `3px solid ${balanceColor}`,
                      border: `1px solid ${colors.primary[300]}`,
                      borderTopColor: balanceColor,
                      transition: "transform 0.15s, box-shadow 0.15s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${balanceColor}33`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: "12px !important" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                        <Box sx={{ color: balanceColor, display: "flex", alignItems: "center" }}>
                          {config.icon}
                        </Box>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          sx={{ fontSize: "0.6rem", height: 18, "& .MuiChip-label": { px: 0.75 } }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[300], display: "block", mb: 0.25, lineHeight: 1.2 }}
                      >
                        {item.leaveType}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          sx={{ color: balanceColor, lineHeight: 1 }}
                        >
                          {item.balance === null ? "—" : balance}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          days
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.balance === null ? 0 : pct}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: colors.primary[300],
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: getProgressColor(balance, config.maxDays),
                            borderRadius: 2,
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                </Tooltip>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: colors.grey[400] }}>
            No leave balances available.
          </Typography>
        )}
      </Box>
      <DynamicTable
        title="Leave Applications"
        subtitle="View all Leave applications, create or update a leave"
        columns={LeaveApplicationsColumns}
        FormComponent={LeaveApplicationForm}
        query={getStructuredLeaveApplications}
        actions={actions}
        base_url={base_url}
        // sort newest records first based on ID (highest ID = newest)
        defaultSortModel={[{ field: "id", sort: "desc" }, { field: "createdDate", sort: "desc" }]}
      />
      <LeaveDocumentsDrawer
        open={docsDrawerOpen}
        onClose={() => {
          setDocsDrawerOpen(false);
          setSelectedRecordForDocs(null);
        }}
        leaveApplicationId={selectedRecordForDocs?.id}
      />
    </Box>
  );
};

export default LeaveApplicationsTable;
